import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useSettings } from "@/contexts/SettingsContext";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Pencil, Trash2, Search, Wrench, Shield, CircleDot, AlertTriangle, Car, Cog, ClipboardCheck, Filter, X } from "lucide-react";
import { toast } from "sonner";
import { formatDate } from "@/utils/dates";

const typeConfig = {
  tagliando: { label: "Tagliando", icon: Wrench, cls: "bg-blue-100 text-blue-800 border-blue-300 dark:bg-blue-900/50 dark:text-blue-200 dark:border-blue-700" },
  gomme: { label: "Gomme", icon: CircleDot, cls: "bg-slate-200 text-slate-800 border-slate-400 dark:bg-slate-700 dark:text-slate-200 dark:border-slate-500" },
  assicurazione: { label: "Assicurazione", icon: Shield, cls: "bg-emerald-100 text-emerald-800 border-emerald-300 dark:bg-emerald-900/50 dark:text-emerald-200 dark:border-emerald-700" },
  revisione: { label: "Revisione", icon: ClipboardCheck, cls: "bg-violet-100 text-violet-800 border-violet-300 dark:bg-violet-900/50 dark:text-violet-200 dark:border-violet-700" },
  meccanica: { label: "Meccanica", icon: Cog, cls: "bg-orange-100 text-orange-800 border-orange-300 dark:bg-orange-900/50 dark:text-orange-200 dark:border-orange-700" },
};

const tagliandoFlags = [
  { key: "flag_olio", label: "Olio" },
  { key: "flag_filtro_olio", label: "Filtro Olio" },
  { key: "flag_filtro_carburante", label: "Filtro Carburante" },
  { key: "flag_filtro_aria", label: "Filtro Aria" },
  { key: "flag_filtro_antipolline", label: "Filtro Antipolline" },
  { key: "flag_distribuzione", label: "Distribuzione" },
];

const emptyForm = {
  vehicle_id: "", type: "tagliando", description: "", date: "", expiry_date: "",
  km: 0, next_km: 0, cost: 0, provider: "", policy_number: "",
  insurance_company: "", tire_type: "", notes: "",
  flag_olio: false, flag_filtro_olio: false, flag_filtro_carburante: false,
  flag_filtro_aria: false, flag_filtro_antipolline: false, flag_distribuzione: false,
};

export default function MaintenancePage() {
  const { axiosAuth } = useAuth();
  const { settings } = useSettings();
  const accent = settings?.primary_color || "#DC2626";
  const [records, setRecords] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [activeTab, setActiveTab] = useState("tutti");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [submitting, setSubmitting] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(null);
  const [selectedVehicle, setSelectedVehicle] = useState("");

  const fetchData = useCallback(async () => {
    try {
      const api = axiosAuth();
      const [mRes, vRes] = await Promise.all([api.get("/maintenance"), api.get("/vehicles")]);
      setRecords(mRes.data);
      setVehicles(vRes.data);
    } catch { toast.error("Errore nel caricamento"); }
    finally { setLoading(false); }
  }, [axiosAuth]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSave = async () => {
    setSubmitting(true);
    try {
      const api = axiosAuth();
      if (editing) {
        const { vehicle_id, type, ...updateData } = form;
        await api.put(`/maintenance/${editing.id}`, updateData);
        toast.success("Record aggiornato");
      } else {
        await api.post("/maintenance", form);
        toast.success("Record aggiunto");
      }
      setDialogOpen(false); setEditing(null); setForm({ ...emptyForm }); fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "Errore"); }
    finally { setSubmitting(false); }
  };

  const handleDelete = async () => {
    try { const api = axiosAuth(); await api.delete(`/maintenance/${deleteDialog.id}`); toast.success("Eliminato"); setDeleteDialog(null); fetchData(); }
    catch { toast.error("Errore"); }
  };

  const openEdit = (r) => {
    setEditing(r);
    setForm({
      vehicle_id: r.vehicle_id, type: r.type, description: r.description || "",
      date: r.date || "", expiry_date: r.expiry_date || "", km: r.km || 0,
      next_km: r.next_km || 0, cost: r.cost || 0, provider: r.provider || "",
      policy_number: r.policy_number || "", insurance_company: r.insurance_company || "",
      tire_type: r.tire_type || "", notes: r.notes || "",
      flag_olio: r.flag_olio || false, flag_filtro_olio: r.flag_filtro_olio || false,
      flag_filtro_carburante: r.flag_filtro_carburante || false, flag_filtro_aria: r.flag_filtro_aria || false,
      flag_filtro_antipolline: r.flag_filtro_antipolline || false, flag_distribuzione: r.flag_distribuzione || false,
    });
    setDialogOpen(true);
  };

  const openNew = (presetType) => { setEditing(null); setForm({ ...emptyForm, type: presetType || "tagliando" }); setDialogOpen(true); };

  const isExpiringSoon = (d) => { if (!d) return false; const diff = (new Date(d) - new Date()) / 86400000; return diff >= 0 && diff <= 30; };
  const isExpired = (d) => d && new Date(d) < new Date();

  const filtered = records.filter((r) => {
    if (selectedVehicle && selectedVehicle !== "all" && r.vehicle_id !== selectedVehicle) return false;
    const q = search.toLowerCase();
    const ms = `${r.vehicle?.brand || ""} ${r.vehicle?.model || ""} ${r.vehicle?.plate || ""} ${r.description || ""} ${r.provider || ""} ${r.insurance_company || ""}`.toLowerCase().includes(q);
    if (activeTab === "tutti") return ms;
    return ms && r.type === activeTab;
  });

  const counts = { tagliando: 0, gomme: 0, assicurazione: 0, revisione: 0, meccanica: 0, expiring: 0 };
  const countSource = (selectedVehicle && selectedVehicle !== "all") ? records.filter(r => r.vehicle_id === selectedVehicle) : records;
  countSource.forEach(r => { counts[r.type] = (counts[r.type] || 0) + 1; if (r.expiry_date && isExpiringSoon(r.expiry_date)) counts.expiring++; });

  const getTagliandoFlags = (r) => tagliandoFlags.filter(f => r[f.key]).map(f => f.label);

  return (
    <div className="space-y-6" data-testid="maintenance-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-outfit font-bold text-slate-900 dark:text-white">Manutenzione Veicoli</h2>
          <p className="text-sm text-slate-500 dark:text-slate-400">{records.length} registrazioni totali</p>
        </div>
        <Button onClick={() => openNew()} data-testid="add-maintenance-button" style={{ backgroundColor: accent }} className="hover:opacity-90 text-white rounded-lg shadow-sm">
          <Plus className="w-4 h-4 mr-2" /> Nuova Registrazione
        </Button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-6 gap-3">
        {[
          { key: "tagliando", icon: Wrench, color: "text-blue-600 dark:text-blue-400", bg: "bg-blue-100 dark:bg-blue-900/40" },
          { key: "gomme", icon: CircleDot, color: "text-slate-600 dark:text-slate-300", bg: "bg-slate-200 dark:bg-slate-700" },
          { key: "assicurazione", icon: Shield, color: "text-emerald-600 dark:text-emerald-400", bg: "bg-emerald-100 dark:bg-emerald-900/40" },
          { key: "revisione", icon: ClipboardCheck, color: "text-violet-600 dark:text-violet-400", bg: "bg-violet-100 dark:bg-violet-900/40" },
          { key: "meccanica", icon: Cog, color: "text-orange-600 dark:text-orange-400", bg: "bg-orange-100 dark:bg-orange-900/40" },
          { key: "expiring", icon: AlertTriangle, color: "text-amber-600 dark:text-amber-400", bg: "bg-amber-100 dark:bg-amber-900/40", label: "In Scadenza" },
        ].map(s => (
          <Card key={s.key} className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 shadow-sm rounded-xl cursor-pointer hover:shadow-md transition-shadow" onClick={() => s.key !== "expiring" && setActiveTab(s.key)}>
            <CardContent className="p-3 flex items-center gap-2.5">
              <div className={`w-8 h-8 rounded-lg ${s.bg} flex items-center justify-center`}><s.icon className={`w-4 h-4 ${s.color}`} /></div>
              <div>
                <p className="text-base font-bold text-slate-900 dark:text-white">{counts[s.key] || 0}</p>
                <p className="text-[10px] text-slate-500 dark:text-slate-400">{s.label || typeConfig[s.key]?.label}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-slate-100 dark:bg-slate-800 p-1 rounded-lg" data-testid="maintenance-tabs">
          <TabsTrigger value="tutti" className="rounded-md text-xs sm:text-sm">Tutti</TabsTrigger>
          <TabsTrigger value="tagliando" className="rounded-md text-xs sm:text-sm">Tagliandi</TabsTrigger>
          <TabsTrigger value="gomme" className="rounded-md text-xs sm:text-sm">Gomme</TabsTrigger>
          <TabsTrigger value="assicurazione" className="rounded-md text-xs sm:text-sm">Assicurazioni</TabsTrigger>
          <TabsTrigger value="revisione" className="rounded-md text-xs sm:text-sm">Revisioni</TabsTrigger>
          <TabsTrigger value="meccanica" className="rounded-md text-xs sm:text-sm">Meccanica</TabsTrigger>
        </TabsList>
      </Tabs>

      <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 shadow-sm rounded-xl">
        <CardHeader className="pb-3">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input placeholder="Cerca per veicolo, fornitore..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 h-10 bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-600 rounded-lg" data-testid="maintenance-search-input" />
            </div>
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-slate-400 shrink-0" />
              <Select value={selectedVehicle} onValueChange={setSelectedVehicle}>
                <SelectTrigger className="h-10 w-[220px] rounded-lg text-sm" data-testid="vehicle-filter">
                  <SelectValue placeholder="Tutti i veicoli" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti i veicoli</SelectItem>
                  {vehicles.map(v => (
                    <SelectItem key={v.id} value={v.id}>{v.brand} {v.model} - {v.plate}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedVehicle && selectedVehicle !== "all" && (
                <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-400 hover:text-slate-600" onClick={() => setSelectedVehicle("")}>
                  <X className="w-4 h-4" />
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50/50 dark:bg-slate-800/50">
                <TableHead className="font-semibold text-slate-700 dark:text-slate-200">Veicolo</TableHead>
                <TableHead className="font-semibold text-slate-700 dark:text-slate-200">Tipo</TableHead>
                <TableHead className="font-semibold text-slate-700 dark:text-slate-200 hidden md:table-cell">Data</TableHead>
                <TableHead className="font-semibold text-slate-700 dark:text-slate-200 hidden lg:table-cell">Scad./KM</TableHead>
                <TableHead className="font-semibold text-slate-700 dark:text-slate-200 hidden lg:table-cell">Dettagli</TableHead>
                <TableHead className="font-semibold text-slate-700 dark:text-slate-200 hidden md:table-cell">Costo</TableHead>
                <TableHead className="font-semibold text-slate-700 dark:text-slate-200 text-right">Azioni</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={7} className="text-center py-8 text-slate-400">Caricamento...</TableCell></TableRow>
              ) : filtered.length === 0 ? (
                <TableRow><TableCell colSpan={7} className="text-center py-8 text-slate-400">{search ? "Nessun record" : "Nessun record. Aggiungi il primo!"}</TableCell></TableRow>
              ) : (
                filtered.map((r) => {
                  const tc = typeConfig[r.type] || typeConfig.tagliando;
                  const expired = isExpired(r.expiry_date);
                  const expiringSoon = !expired && isExpiringSoon(r.expiry_date);
                  const flags = r.type === "tagliando" ? getTagliandoFlags(r) : [];
                  return (
                    <TableRow key={r.id} className="hover:bg-slate-50/50 dark:hover:bg-slate-800/50" data-testid={`maintenance-row-${r.id}`}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 bg-slate-100 dark:bg-slate-700 rounded-md flex items-center justify-center"><Car className="w-3.5 h-3.5 text-slate-500 dark:text-slate-300" /></div>
                          <div>
                            <p className="font-medium text-slate-900 dark:text-white text-sm">{r.vehicle?.brand} {r.vehicle?.model}</p>
                            <p className="text-[10px] font-mono text-slate-400">{r.vehicle?.plate}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell><Badge className={`${tc.cls} border text-xs`}>{tc.label}</Badge></TableCell>
                      <TableCell className="hidden md:table-cell text-sm text-slate-700 dark:text-slate-200">{formatDate(r.date) || "-"}</TableCell>
                      <TableCell className="hidden lg:table-cell">
                        {r.type === "gomme" || r.type === "meccanica" ? (
                          <span className="text-sm text-slate-700 dark:text-slate-200">{r.km > 0 ? `${r.km.toLocaleString()} km` : "-"}</span>
                        ) : r.type === "revisione" ? (
                          <div className="flex flex-col gap-0.5">
                            {r.expiry_date ? (
                              <div className="flex items-center gap-1.5">
                                <span className={`text-sm ${expired ? "text-red-500 font-semibold" : expiringSoon ? "text-amber-500 font-semibold" : "text-slate-700 dark:text-slate-200"}`}>{formatDate(r.expiry_date)}</span>
                                {expired && <Badge className="bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300 border-red-300 dark:border-red-700 text-[10px] border">Scaduta</Badge>}
                                {expiringSoon && <Badge className="bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300 border-amber-300 dark:border-amber-700 text-[10px] border">In Scadenza</Badge>}
                              </div>
                            ) : "-"}
                            {r.km > 0 && <span className="text-[10px] text-slate-400">{r.km.toLocaleString()} km</span>}
                          </div>
                        ) : r.expiry_date ? (
                          <div className="flex items-center gap-1.5">
                            <span className={`text-sm ${expired ? "text-red-500 font-semibold" : expiringSoon ? "text-amber-500 font-semibold" : "text-slate-700 dark:text-slate-200"}`}>{formatDate(r.expiry_date)}</span>
                            {expired && <Badge className="bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300 border-red-300 dark:border-red-700 text-[10px] border">Scaduta</Badge>}
                            {expiringSoon && <Badge className="bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300 border-amber-300 dark:border-amber-700 text-[10px] border">In Scadenza</Badge>}
                          </div>
                        ) : "-"}
                      </TableCell>
                      <TableCell className="hidden lg:table-cell text-sm text-slate-600 dark:text-slate-300">
                        {r.type === "tagliando" && flags.length > 0 ? (
                          <div className="flex flex-wrap gap-1">{flags.map(f => <Badge key={f} variant="outline" className="text-[10px] py-0 px-1.5 dark:border-slate-600 dark:text-slate-300">{f}</Badge>)}</div>
                        ) : r.type === "assicurazione" && r.insurance_company ? r.insurance_company
                          : r.type === "revisione" ? (r.provider || "-")
                          : r.type === "gomme" && r.tire_type ? r.tire_type
                          : r.type === "meccanica" ? (r.description || "-")
                          : r.provider || r.description || "-"}
                      </TableCell>
                      <TableCell className="hidden md:table-cell text-sm font-semibold text-slate-900 dark:text-white">{r.cost > 0 ? `${r.cost.toFixed(2)} EUR` : "-"}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button variant="ghost" size="icon" onClick={() => openEdit(r)} className="h-7 w-7 text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-white"><Pencil className="w-3 h-3" /></Button>
                          <Button variant="ghost" size="icon" onClick={() => setDeleteDialog(r)} className="h-7 w-7 text-slate-500 dark:text-slate-400 hover:text-red-600"><Trash2 className="w-3 h-3" /></Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg rounded-xl bg-white dark:bg-slate-900" data-testid="maintenance-dialog">
          <DialogHeader>
            <DialogTitle className="font-outfit text-slate-900 dark:text-white">{editing ? "Modifica Record" : "Nuova Registrazione"}</DialogTitle>
            <DialogDescription>{editing ? "Modifica i dati" : "Registra un nuovo intervento"}</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 max-h-[60vh] overflow-y-auto pr-1">
            {!editing && (
              <>
                <div className="space-y-2 col-span-2">
                  <Label className="text-slate-700 dark:text-slate-200">Veicolo *</Label>
                  <Select value={form.vehicle_id} onValueChange={(v) => setForm({ ...form, vehicle_id: v })}>
                    <SelectTrigger className="h-10 rounded-lg"><SelectValue placeholder="Seleziona veicolo" /></SelectTrigger>
                    <SelectContent>{vehicles.map(v => <SelectItem key={v.id} value={v.id}>{v.brand} {v.model} - {v.plate}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2 col-span-2">
                  <Label className="text-slate-700 dark:text-slate-200">Tipo *</Label>
                  <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                    <SelectTrigger className="h-10 rounded-lg"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="tagliando">Tagliando</SelectItem>
                      <SelectItem value="gomme">Gomme</SelectItem>
                      <SelectItem value="assicurazione">Assicurazione</SelectItem>
                      <SelectItem value="revisione">Revisione</SelectItem>
                      <SelectItem value="meccanica">Meccanica</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}
            <div className="space-y-2"><Label className="text-slate-700 dark:text-slate-200">Data Intervento</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="h-10 rounded-lg" /></div>
            {(form.type === "assicurazione" || form.type === "tagliando" || form.type === "revisione") && (
              <div className="space-y-2"><Label className="text-slate-700 dark:text-slate-200">Data Scadenza</Label><Input type="date" value={form.expiry_date} onChange={(e) => setForm({ ...form, expiry_date: e.target.value })} className="h-10 rounded-lg" /></div>
            )}

            {/* Revisione */}
            {form.type === "revisione" && (
              <>
                <div className="space-y-2"><Label className="text-slate-700 dark:text-slate-200">KM alla Revisione</Label><Input type="number" value={form.km} onChange={(e) => setForm({ ...form, km: parseInt(e.target.value) || 0 })} className="h-10 rounded-lg" /></div>
                <div className="space-y-2"><Label className="text-slate-700 dark:text-slate-200">Centro Revisione</Label><Input value={form.provider} onChange={(e) => setForm({ ...form, provider: e.target.value })} placeholder="Nome centro" className="h-10 rounded-lg" /></div>
              </>
            )}

            {/* Tagliando */}
            {form.type === "tagliando" && (
              <>
                <div className="space-y-2"><Label className="text-slate-700 dark:text-slate-200">Km al Tagliando</Label><Input type="number" value={form.km} onChange={(e) => setForm({ ...form, km: parseInt(e.target.value) || 0 })} className="h-10 rounded-lg" /></div>
                <div className="space-y-2"><Label className="text-slate-700 dark:text-slate-200">Km Prossimo</Label><Input type="number" value={form.next_km} onChange={(e) => setForm({ ...form, next_km: parseInt(e.target.value) || 0 })} className="h-10 rounded-lg" /></div>
                <div className="space-y-2 col-span-2"><Label className="text-slate-700 dark:text-slate-200">Officina</Label><Input value={form.provider} onChange={(e) => setForm({ ...form, provider: e.target.value })} placeholder="Nome officina" className="h-10 rounded-lg" /></div>
                <div className="space-y-3 col-span-2 p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                  <Label className="text-slate-700 dark:text-slate-200 font-semibold text-sm">Interventi Effettuati</Label>
                  <div className="grid grid-cols-2 gap-2.5">
                    {tagliandoFlags.map(f => (
                      <div key={f.key} className="flex items-center gap-2">
                        <Checkbox id={f.key} checked={form[f.key]} onCheckedChange={(checked) => setForm({ ...form, [f.key]: !!checked })} data-testid={`flag-${f.key}`} />
                        <Label htmlFor={f.key} className="text-sm font-normal text-slate-700 dark:text-slate-300 cursor-pointer">{f.label}</Label>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}

            {/* Gomme */}
            {form.type === "gomme" && (
              <>
                <div className="space-y-2 col-span-2">
                  <Label className="text-slate-700 dark:text-slate-200">Tipo Gomme</Label>
                  <Select value={form.tire_type} onValueChange={(v) => setForm({ ...form, tire_type: v })}>
                    <SelectTrigger className="h-10 rounded-lg"><SelectValue placeholder="Seleziona tipo" /></SelectTrigger>
                    <SelectContent><SelectItem value="Estive">Estive</SelectItem><SelectItem value="Invernali">Invernali</SelectItem><SelectItem value="4 Stagioni">4 Stagioni</SelectItem></SelectContent>
                  </Select>
                </div>
                <div className="space-y-2"><Label className="text-slate-700 dark:text-slate-200">KM al Cambio</Label><Input type="number" value={form.km} onChange={(e) => setForm({ ...form, km: parseInt(e.target.value) || 0 })} className="h-10 rounded-lg" /></div>
                <div className="space-y-2"><Label className="text-slate-700 dark:text-slate-200">Gommista</Label><Input value={form.provider} onChange={(e) => setForm({ ...form, provider: e.target.value })} placeholder="Nome gommista" className="h-10 rounded-lg" /></div>
              </>
            )}

            {/* Assicurazione */}
            {form.type === "assicurazione" && (
              <>
                <div className="space-y-2"><Label className="text-slate-700 dark:text-slate-200">Compagnia</Label><Input value={form.insurance_company} onChange={(e) => setForm({ ...form, insurance_company: e.target.value })} placeholder="es. UnipolSai" className="h-10 rounded-lg" /></div>
                <div className="space-y-2"><Label className="text-slate-700 dark:text-slate-200">N. Polizza</Label><Input value={form.policy_number} onChange={(e) => setForm({ ...form, policy_number: e.target.value })} placeholder="Numero polizza" className="h-10 rounded-lg font-mono" /></div>
              </>
            )}

            {/* Meccanica */}
            {form.type === "meccanica" && (
              <>
                <div className="space-y-2"><Label className="text-slate-700 dark:text-slate-200">KM</Label><Input type="number" value={form.km} onChange={(e) => setForm({ ...form, km: parseInt(e.target.value) || 0 })} className="h-10 rounded-lg" /></div>
                <div className="space-y-2"><Label className="text-slate-700 dark:text-slate-200">Officina</Label><Input value={form.provider} onChange={(e) => setForm({ ...form, provider: e.target.value })} placeholder="Nome officina" className="h-10 rounded-lg" /></div>
                <div className="space-y-2 col-span-2"><Label className="text-slate-700 dark:text-slate-200">Descrizione Intervento *</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Descrivi l'intervento meccanico..." className="h-10 rounded-lg" /></div>
              </>
            )}

            <div className="space-y-2"><Label className="text-slate-700 dark:text-slate-200">Costo (EUR)</Label><Input type="number" step="0.01" value={form.cost} onChange={(e) => setForm({ ...form, cost: parseFloat(e.target.value) || 0 })} className="h-10 rounded-lg" /></div>
            {form.type !== "meccanica" && <div className="space-y-2"><Label className="text-slate-700 dark:text-slate-200">Note</Label><Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Note..." className="h-10 rounded-lg" /></div>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="rounded-lg">Annulla</Button>
            <Button onClick={handleSave} disabled={submitting || (!editing && !form.vehicle_id)} data-testid="maintenance-save-button" style={{ backgroundColor: accent }} className="hover:opacity-90 text-white rounded-lg">
              {submitting ? "Salvataggio..." : "Salva"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleteDialog} onOpenChange={() => setDeleteDialog(null)}>
        <DialogContent className="sm:max-w-md rounded-xl bg-white dark:bg-slate-900">
          <DialogHeader><DialogTitle className="font-outfit text-slate-900 dark:text-white">Elimina Record</DialogTitle><DialogDescription>Confermi?</DialogDescription></DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog(null)} className="rounded-lg">Annulla</Button>
            <Button onClick={handleDelete} className="bg-red-600 hover:bg-red-700 text-white rounded-lg">Elimina</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
